// File: frontend/src/pages/Plato/Plato.js

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../config/constants';
import { useLanguage } from '../../contexts/LanguageContext';
import { useHeader } from '../../contexts/HeaderContext';
import { useUser } from '../../contexts/UserContext';
import styles from './Plato.module.css';

// Components
import KnowledgeContainer from './components/KnowledgeContainer/KnowledgeContainer';
import KnowledgeInput from './components/KnowledgeInput/KnowledgeInput';
import TemplateSelector from './components/TemplateSelector/TemplateSelector';
import DomainSelector from './components/DomainSelector/DomainSelector';
import CapabilityMatrix from './components/CapabilityMatrix/CapabilityMatrix';
import ParameterSelector from './components/ParameterSelector/ParameterSelector';

// Config
import platoConfig, { templates as defaultTemplates, domains as defaultDomains } from './config/platoConfig';

// Default configurations - using actual template IDs and agent names
const DEFAULT_TEMPLATE = {
  id: '2c806623-dc74-4f8b-961b-c7dd82529dd4',
  name: 'Technical Analysis Template',
  description: 'Designed for detailed technical analysis with formal tone and analytical approach',
  icon: 'BarChart'
};

const DEFAULT_DOMAIN = {
  id: 'plato_general',
  key: 'ai_plato_general',
  name: 'General Knowledge',
  description: 'Cross-domain knowledge transfer and amplification for general insurance concepts',
  icon: 'Globe'
};

// Helper functions for knowledge handling
const createUserInput = (input) => ({
  role: 'user',
  content: [{
    type: 'text',
    text: input
  }],
  timestamp: new Date().toISOString()
});

const createTypingIndicator = () => ({
  role: 'assistant',
  content: [{
    type: 'text',
    text: ''
  }],
  isTyping: true,
  timestamp: new Date().toISOString()
});

const createAssistantResponse = (response) => ({
  role: 'assistant',
  content: response.content || [{ type: 'text', text: response.message }],
  timestamp: new Date().toISOString(),
  usage: response.usage
});

const Plato = ({ onMount, ...props }) => {
  const { t } = useLanguage();
  const { knowledge_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateChatControls } = useHeader();
  const inputContainerRef = useRef(null);

  // Core states
  const [knowledgeId, setKnowledgeId] = useState(knowledge_id);
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingKnowledge, setIsFetchingKnowledge] = useState(false);
  const [isInputProcessing, setIsInputProcessing] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [outputTitle, setOutputTitle] = useState('');
  const [outputSummary, setOutputSummary] = useState('');

  // Get initial state from navigation if available
  const initialState = location.state || {};
  
  // UI states
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showDomainSelector, setShowDomainSelector] = useState(false);
  const [showCapabilityMatrix, setShowCapabilityMatrix] = useState(false);
  const [showParameterSelector, setShowParameterSelector] = useState(false);
  
  // Template and Domain states
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    return initialState.template || 
           JSON.parse(localStorage.getItem('selectedTemplate')) || 
           DEFAULT_TEMPLATE;
  });

  const [selectedDomain, setSelectedDomain] = useState(() => {
    return initialState.domain ||
           JSON.parse(localStorage.getItem('selectedDomain')) || 
           DEFAULT_DOMAIN;
  });

  // Parameters state
  const [parameters, setParameters] = useState(() => {
    const savedParams = localStorage.getItem('platoParameters');
    if (savedParams) {
      return JSON.parse(savedParams);
    }
    
    const initialParameters = {};
    Object.entries(platoConfig.parameters).forEach(([key, config]) => {
      initialParameters[key] = config.type === 'object' ? {} : config.default;
    });
    return initialParameters;
  });

  useEffect(() => {
    const handleInitialInput = async () => {
      if (initialState.initialInput && !knowledgeId) {
        await handleProcessInput(initialState.initialInput);
        // Clear the navigation state after handling the input
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    handleInitialInput();
  }, [initialState.initialInput]);

  // Template selection handler
  const handleTemplateSelect = (template) => {
    console.log('Selected template:', template);
    setSelectedTemplate(template);
    localStorage.setItem('selectedTemplate', JSON.stringify(template));
  };

  // Domain selection handler
  const handleDomainSelect = (domain) => {
    console.log('Selected domain:', domain);
    setSelectedDomain(domain);
    localStorage.setItem('selectedDomain', JSON.stringify(domain));
  };

  // Parameter change handler
  const handleParameterChange = (paramName, value) => {
    setParameters(prev => {
      const newParams = { ...prev, [paramName]: value };
      localStorage.setItem('platoParameters', JSON.stringify(newParams));
      return newParams;
    });
  };

  const handleNewKnowledge = useCallback(() => {
    setKnowledgeId(null);
    setKnowledgeEntries([]);
    setOutputTitle('');
    setOutputSummary('');
    // Reset template and domain to defaults
    setSelectedTemplate(DEFAULT_TEMPLATE);
    setSelectedDomain(DEFAULT_DOMAIN);
    // Also update localStorage
    localStorage.setItem('selectedTemplate', JSON.stringify(DEFAULT_TEMPLATE));
    localStorage.setItem('selectedDomain', JSON.stringify(DEFAULT_DOMAIN));
    // Navigate to base Plato route
    navigate('/plato');
  }, [navigate]);

  // Share knowledge handler
  const handleShareKnowledge = useCallback(async (type) => {
    if (!knowledgeId) return;
    
    if (type === 'email') {
      try {
        // Using the existing chat API endpoint for sharing
        await apiService.post(API_ENDPOINTS.AGENTS.CHAT.SHARE_EMAIL, {
          chat_id: knowledgeId
        });
        // You might want to show a success notification here
      } catch (error) {
        console.error('Error sharing via email:', error);
        // Handle error appropriately
      }
    } else if (type === 'download') {
      try {
        // Fetch complete knowledge data if needed
        const knowledgeData = {
          knowledge_id: knowledgeId,
          entries: knowledgeEntries,
          title: outputTitle,
          summary: outputSummary,
          template: selectedTemplate,
          domain: selectedDomain,
          parameters: parameters,
          timestamp: new Date().toISOString()
        };
        
        const blob = new Blob([JSON.stringify(knowledgeData, null, 2)], { 
          type: 'application/json' 
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `knowledge-${knowledgeId}-${new Date().toISOString()}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading knowledge:', error);
        // Handle error appropriately
      }
    }
  }, [knowledgeId, knowledgeEntries, outputTitle, outputSummary, selectedTemplate, selectedDomain, parameters]);

  // Memoize the controls object itself
  const knowledgeControls = useMemo(() => ({
    knowledgeId,
    onNewKnowledge: handleNewKnowledge,
    onShareKnowledge: handleShareKnowledge,
    showHistoryButton: true
  }), [knowledgeId, handleNewKnowledge, handleShareKnowledge]);

  // Update header controls only when the memoized object changes
  useEffect(() => {
    updateChatControls(knowledgeControls);
  }, [knowledgeControls, updateChatControls]);

  // Knowledge initialization handler - using the existing chat API
  const initializeKnowledge = async () => {
    try {
      const knowledgeResponse = await apiService.post(API_ENDPOINTS.AGENTS.CHAT.INITIATE, {
        template_id: selectedTemplate.id, // Using the UUID template ID
        agent_name: selectedDomain.key, // Using the agent name from your list
        training_mode: false,
        source: 'ui'
      });

      const newKnowledgeId = knowledgeResponse.chat_id;
      navigate(`/plato/knowledge/${newKnowledgeId}`, { replace: true });
      setKnowledgeId(newKnowledgeId);
      
      return newKnowledgeId;
    } catch (error) {
      console.error('Error initializing knowledge:', error);
      throw error;
    }
  };

  // Input processing handler - using the existing chat API
  const processInput = async (knowledgeId, input) => {
  try {
    // Format parameters in a more structured way for the AI
    const formattedParameters = Object.entries(parameters).map(([key, value]) => {
      if (key === 'tone' && Array.isArray(value) && value.length === 2) {
        return `${key}: ${value[0]} (intensity: ${value[1] * 100}%)`;
      }
      return `${key}: ${JSON.stringify(value)}`;
    }).join('\n');

    // Add parameters to the message for processing
    const messageWithParams = `
[PLATO PARAMETERS]
${formattedParameters}
[/PLATO PARAMETERS]

${input}`;

    console.log('Sending message to API:', { chat_id: knowledgeId, message: messageWithParams });
    
    const response = await apiService.post(API_ENDPOINTS.AGENTS.CHAT.MESSAGE, {
      chat_id: knowledgeId,
      message: messageWithParams
    });
      
      // Extract title and summary from the response if available
      // This assumes the AI might return structured data in its response
      try {
        const responseText = response.message;
        
        // Look for title pattern
        const titleMatch = responseText.match(/Title:\s*([^\n]+)/i);
        if (titleMatch && titleMatch[1]) {
          setOutputTitle(titleMatch[1].trim());
        }
        
        // Look for summary pattern
        const summaryMatch = responseText.match(/Summary:\s*([^\n]+(?:\n[^\n]+)*)/i);
        if (summaryMatch && summaryMatch[1]) {
          setOutputSummary(summaryMatch[1].trim());
        }
      } catch (parseError) {
        console.error('Error parsing title/summary from response:', parseError);
      }
      
      return response;
    } catch (error) {
      console.error('Error processing input:', error);
      throw error;
    }
  };

  // Main input handler
  const handleProcessInput = async (input) => {
    if (!input.trim()) return;

    setIsInputProcessing(true); // Set at start of processing
    const userInput = createUserInput(input);
    const typingIndicator = createTypingIndicator();

    try {
      // Immediately update UI with user input and typing indicator
      setKnowledgeEntries(prev => [...prev, userInput, typingIndicator]);

      if (!knowledgeId) {
        // Initialize knowledge but don't wait for navigation
        const knowledgeResponse = await apiService.post(API_ENDPOINTS.AGENTS.CHAT.INITIATE, {
          template_id: selectedTemplate.id, // Using the UUID template ID
          agent_name: selectedDomain.key, // Using the agent name from your list
          training_mode: false,
          source: 'ui'
        });

        const newKnowledgeId = knowledgeResponse.chat_id;
        
        // Set navigating state before changing URL
        setIsNavigating(true);
        
        // Update URL without causing a reload
        navigate(`/plato/knowledge/${newKnowledgeId}`, { 
          replace: true,
          state: { 
            entries: [...knowledgeEntries, userInput],
            preserveState: true 
          }
        });
        
        setKnowledgeId(newKnowledgeId);

        // Process input with new knowledge ID
        const processResponse = await processInput(newKnowledgeId, input);
        
        if (processResponse) {
          // Update entries, replacing typing indicator
          setKnowledgeEntries(prev => [
            ...prev.filter(entry => !entry.isTyping),
            createAssistantResponse(processResponse)
          ]);
        }
      } else {
        // Subsequent inputs flow
        const processResponse = await processInput(knowledgeId, input);
        
        if (processResponse) {
          setKnowledgeEntries(prev => [
            ...prev.filter(entry => !entry.isTyping),
            createAssistantResponse(processResponse)
          ]);
        }
      }
    } catch (error) {
      console.error('Error processing input:', error);
      // Remove typing indicator on error
      setKnowledgeEntries(prev => prev.filter(entry => !entry.isTyping));
    } finally {
      setIsInputProcessing(false);
      setIsNavigating(false);
    }
  };

  // Fetch existing knowledge if knowledge_id exists - using the existing chat API
  useEffect(() => {
    if (knowledge_id && !isNavigating) {
      const fetchKnowledge = async () => {
        try {
          setIsFetchingKnowledge(true);
          const response = await apiService.get(API_ENDPOINTS.AGENTS.CHAT.GET(knowledge_id));
          
          if (response) {
            setKnowledgeId(knowledge_id);
            // Only update entries if we don't already have them
            if (!knowledgeEntries.length) {
              setKnowledgeEntries(response.messages || []);
            }
            
            // Try to extract title and summary from the first assistant message
            if (response.messages && response.messages.length > 0) {
              const assistantMessages = response.messages.filter(msg => msg.role === 'assistant');
              if (assistantMessages.length > 0) {
                const firstMessage = assistantMessages[0];
                const messageText = firstMessage.content[0]?.text || '';
                
                // Look for title pattern
                const titleMatch = messageText.match(/Title:\s*([^\n]+)/i);
                if (titleMatch && titleMatch[1]) {
                  setOutputTitle(titleMatch[1].trim());
                }
                
                // Look for summary pattern
                const summaryMatch = messageText.match(/Summary:\s*([^\n]+(?:\n[^\n]+)*)/i);
                if (summaryMatch && summaryMatch[1]) {
                  setOutputSummary(summaryMatch[1].trim());
                }
              }
            }
            
            // Find the matching template and domain from our defaults
            const templateId = response.template_id;
            const matchingTemplate = defaultTemplates.find(t => t.id === templateId) || DEFAULT_TEMPLATE;
            setSelectedTemplate(matchingTemplate);
            
            const agentName = response.agent_name;
            const matchingDomain = defaultDomains.find(d => d.key === agentName) || DEFAULT_DOMAIN;
            setSelectedDomain(matchingDomain);
          }
        } catch (error) {
          console.error('Error fetching knowledge:', error);
          navigate('/plato');
        } finally {
          setIsFetchingKnowledge(false);
        }
      };

      fetchKnowledge();
    }
  }, [knowledge_id, isNavigating]);

  // Loading state handler
  if (isFetchingKnowledge && !knowledgeEntries.length) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        {t('loading')}
      </div>
    );
  }

  return (
    <div className={styles.platoContainer}>
      <KnowledgeContainer 
        entries={knowledgeEntries} 
        title={outputTitle}
        summary={outputSummary}
        isLoading={isLoading}
      />
      
      <KnowledgeInput
        ref={inputContainerRef}
        onProcessInput={handleProcessInput}
        selectedTemplate={selectedTemplate}
        onTemplateClick={() => setShowTemplateSelector(true)}
        selectedDomain={selectedDomain}
        onDomainClick={() => setShowDomainSelector(true)}
        onCapabilityClick={() => setShowCapabilityMatrix(true)}
        onParameterClick={() => setShowParameterSelector(true)}
        disabled={isLoading}
        knowledgeId={knowledgeId}
        isInputProcessing={isInputProcessing}
      />

      {showTemplateSelector && (
        <TemplateSelector
          templates={defaultTemplates}
          selectedTemplate={selectedTemplate}
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {showDomainSelector && (
        <DomainSelector
          domains={defaultDomains}
          selectedDomain={selectedDomain}
          onSelect={handleDomainSelect}
          onClose={() => setShowDomainSelector(false)}
        />
      )}
      
      {showCapabilityMatrix && (
        <CapabilityMatrix
          capabilities={platoConfig.capabilities}
          onClose={() => setShowCapabilityMatrix(false)}
        />
      )}
      
      {showParameterSelector && (
        <ParameterSelector
          parameters={platoConfig.parameters}
          selectedParameters={parameters}
          onParameterChange={handleParameterChange}
          onClose={() => setShowParameterSelector(false)}
        />
      )}
    </div>
  );
};

export default Plato;